import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ComponentBase } from '../../_utils/component.base';
import { GenericFunctions } from '../../_utils/generic.functions';
import { VideoControlsType } from '../../_utils/custom-types';
import { SafeUrl } from '@angular/platform-browser';

export declare type StyleVideo = 'rounded' | 'rectangle';
export declare type EventTypeHandler = 'waiting' | 'canplay';
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'custom-video',
  templateUrl: './custom-video.component.html',
  styleUrls: ['./custom-video.component.scss'],
})
export class CustomVideoComponent extends ComponentBase implements OnInit {
  @Input()
  public urlVideo: string | SafeUrl;
  @Input()
  public styleVideo: StyleVideo;
  @Input()
  public controls: VideoControlsType;
  @Input()
  public enableSkeleton: boolean;
  @Input()
  public radio: number;
  @Input()
  public heightClassSkeleton: string;
  @Input()
  public loop: boolean;
  @Input()
  public customPauseAndPlayIcons: boolean;
  @Output()
  public endVideoHandler: EventEmitter<void>;
  @Output()
  public loadVideoHandler: EventEmitter<void>;
  @Output()
  public changeTimeHandler: EventEmitter<number>;
  @Output()
  public changeVolumeHandler: EventEmitter<any>;
  @Output()
  public seekedHandler: EventEmitter<number>;
  @Output()
  public pauseHandler: EventEmitter<void>;
  @Output()
  public playHandler: EventEmitter<void>;
  @Output()
  public restartVideoHandler: EventEmitter<void>;
  @Output()
  public eventTypeHandler: EventEmitter<EventTypeHandler>;
  @ViewChild('customVideo')
  public video: ElementRef;
  public loadedVideo: boolean;
  public paused: boolean;
  public isPlaying: boolean;
  public hasPlayedOnce: boolean;
  protected mouseOver: boolean;
  protected playButton: boolean;
  protected isExpanded: boolean;
  public showControlIcons: boolean;

  constructor() {
    super();
    this.initVariables();
  }

  ngOnInit() {
  }

  public changeSeeked(): void {
    this.seekedHandler.emit(this.video.nativeElement.currentTime);
  }

  public changeTime(): void {
    this.changeTimeHandler.emit(this.video.nativeElement.currentTime);
  }

  public changeVolume(): void {
    this.changeVolumeHandler.emit({
      volume: this.video.nativeElement.volume,
      muted: this.video.nativeElement.muted
    });
  }

  public videoEnd(id: string) {
    if (this.isExpanded) {
      if (this.video.nativeElement.exitFullscreen) {
        this.video.nativeElement.exitFullscreen();
      } else if (this.video.nativeElement.webkitExitFullscreen) {
        this.video.nativeElement.webkitExitFullscreen();
      } else if (this.video.nativeElement.mozCancelFullscreen) {
        this.video.nativeElement.mozCancelFullscreen();
      }
    }
    this.video.nativeElement.pause();
    this.video.nativeElement.currentTime = 0;
    this.endVideoHandler.emit();
  }

  public async loadVideo(): Promise<void> {
    if (this.loadedVideo) return;
    this.loadedVideo = true;
    try {
      await GenericFunctions.onLoadDuration(this.video.nativeElement);
    } catch (e) {
      // Duration may not be available on mobile — continue anyway
    }
    this.loadVideoHandler.emit();

    this.video.nativeElement.addEventListener('contextmenu', e => {
      e.preventDefault();
    });
  }

  public pause(): void {
    this.video?.nativeElement?.pause();
  }

  public play(): void {
    this.playButton = false;
    const videoEl = this.video?.nativeElement;
    if (!videoEl) return;

    if (videoEl.readyState < 2) {
      videoEl.load();
      const onReady = () => {
        videoEl.removeEventListener('canplay', onReady);
        this.startPlayback(videoEl);
      };
      videoEl.addEventListener('canplay', onReady);
    } else {
      this.startPlayback(videoEl);
    }
  }

  private startPlayback(videoEl: HTMLVideoElement): void {
    const playPromise = videoEl.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        if (!this.loadedVideo) {
          this.loadVideo();
        }
      }).catch(() => {
        this.playButton = true;
      });
    }
  }

  protected onPlay(): void {
    this.paused = false;
    this.isPlaying = true;
    this.hasPlayedOnce = true;
    this.playHandler.emit();
  }

  protected onPause(): void {
    this.paused = true;
    this.isPlaying = false;
    this.pauseHandler.emit();
  }

  protected videoEventHandler(eventType: EventTypeHandler): void {
    this.eventTypeHandler.emit(eventType);
  }

  public onCanPlay(): void {
    if (!this.loadedVideo) {
      this.loadVideo();
    }
    this.videoEventHandler('canplay');
  }

  private initVariables() {
    this.endVideoHandler = new EventEmitter<void>();
    this.loadVideoHandler = new EventEmitter<void>();
    this.changeTimeHandler = new EventEmitter<number>();
    this.changeVolumeHandler = new EventEmitter<any>();
    this.seekedHandler = new EventEmitter<number>();
    this.pauseHandler = new EventEmitter<void>();
    this.playHandler = new EventEmitter<void>();
    this.restartVideoHandler = new EventEmitter<void>();
    this.eventTypeHandler = new EventEmitter<EventTypeHandler>();
    this.loadedVideo = false;
    this.styleVideo = 'rectangle';
    this.paused = true;
    this.isPlaying = false;
    this.hasPlayedOnce = false;
    this.enableSkeleton = true;
    this.heightClassSkeleton = 'h-[300px]';
    this.radio = 125;
    this.playButton = false;
    this.showControlIcons = false;
    this.controls = 'default';
    this.loop = false;
  }

  showIcons(show: boolean) {
    if (show) this.showControlIcons= true;
    else this.showControlIcons = false;
  }

  restartVideo() {
    this.video.nativeElement.pause();
    this.video.nativeElement.currentTime = 0;
    this.video.nativeElement.play();
    this.paused = false;
    this.restartVideoHandler.emit();
  }

  rewindVideo() {
    this.video.nativeElement.pause();
    if (this.video.nativeElement.currentTime - 5 < 0) {
      this.video.nativeElement.currentTime = 0;
      this.video.nativeElement.play();
    } else {
      this.video.nativeElement.currentTime = this.video.nativeElement.currentTime - 5;
      this.video.nativeElement.play();
    }
    this.paused = false;
  }

  videoSpeed(rate) {
    this.video.nativeElement.playbackRate = rate;
    this.video.nativeElement.play();
    this.paused = false;
  }

  expandVideo() {
    this.isExpanded = true;
    if (this.video.nativeElement.requestFullscreen) this.video.nativeElement.requestFullscreen();
    else if (this.video.nativeElement.mozRequestFullScreen) this.video.nativeElement.mozRequestFullScreen();
    else if (this.video.nativeElement.webkitRequestFullscreen) this.video.nativeElement.webkitRequestFullscreen();
    else if (this.video.nativeElement.msRequestFullscreen) this.video.nativeElement.msRequestFullscreen();
  }

}
